import { z } from "zod";
export const roleIds = [
  "tenant.editor",
  "tenant.tester",
  "tenant.asset-manager",
  "tenant.administrator",
] as const;
export const capabilityIds = [
  "tenant.documents.view",
  "tenant.documents.manage",
  "tenant.templates.publish",
  "tenant.proofs.use",
  "tenant.proofs.generate",
  "tenant.assets.use",
  "tenant.assets.manage",
  "tenant.resources.manage",
  "tenant.fonts.manage",
  "tenant.renders.operate",
  "tenant.integration-data.use",
  "tenant.setup.manage",
  "tenant.users.manage",
  "tenant.roles.manage",
  "tenant.help.view",
  "tenant.audit.view",
] as const;
export type RoleId = (typeof roleIds)[number];
export type CapabilityId = (typeof capabilityIds)[number];
const granted: Record<RoleId, readonly CapabilityId[]> = {
  "tenant.editor": [
    "tenant.documents.view",
    "tenant.documents.manage",
    "tenant.proofs.use",
    "tenant.assets.use",
    "tenant.help.view",
  ],
  "tenant.tester": [
    "tenant.documents.view",
    "tenant.proofs.use",
    "tenant.proofs.generate",
    "tenant.assets.use",
    "tenant.help.view",
  ],
  "tenant.asset-manager": [
    "tenant.assets.use",
    "tenant.assets.manage",
    "tenant.resources.manage",
    "tenant.fonts.manage",
    "tenant.help.view",
  ],
  "tenant.administrator": capabilityIds,
};
export const roleDefinitions = roleIds.map((id) => ({
  id,
  name: {
    "tenant.editor": "Editor",
    "tenant.tester": "Tester",
    "tenant.asset-manager": "Asset Manager",
    "tenant.administrator": "Tenant Administrator",
  }[id],
  capabilities: capabilityIds.filter((c) => granted[id].includes(c)),
  nonCapabilities: capabilityIds.filter((c) => !granted[id].includes(c)),
}));
export const scopedId = z
  .string()
  .min(1)
  .max(200)
  .refine((v) => v.trim() === v && !/[\u0000-\u001f]/.test(v));
export const canonicalRoles = z
  .array(z.enum(roleIds))
  .max(4)
  .refine(
    (v) =>
      JSON.stringify(v) ===
      JSON.stringify(roleIds.filter((r) => v.includes(r))),
    "Roles must be canonical and unique",
  );
export function capabilityUnion(roles: readonly RoleId[]) {
  return capabilityIds.filter((c) => roles.some((r) => granted[r].includes(c)));
}
export const assignmentSchema = z
  .object({
    version: z.literal(1),
    tenantId: scopedId,
    subjectId: scopedId,
    roleIds: canonicalRoles,
    assignmentRevision: z.number().int().positive(),
    authorizationRevision: z.number().int().positive(),
  })
  .strict();
export type Assignment = z.infer<typeof assignmentSchema>;
export const authorizationSnapshotSchema = assignmentSchema
  .extend({
    capabilities: z.array(z.enum(capabilityIds)),
    capabilityCeiling: z.array(z.enum(capabilityIds)).optional(),
  })
  .refine((s) => {
    const expected = capabilityUnion(s.roleIds).filter(
      (c) => !s.capabilityCeiling || s.capabilityCeiling.includes(c),
    );
    const actual = JSON.stringify(s.capabilities);
    if (actual === JSON.stringify(expected)) return true;
    // Version-one retained actors predate this permission. Keep their exact
    // older grant set and immutable job hashes; never add the new permission.
    return (
      s.capabilityCeiling === undefined &&
      actual ===
        JSON.stringify(
          expected.filter((c) => c !== "tenant.integration-data.use"),
        )
    );
  }, "Invalid capability snapshot");
export type AuthorizationSnapshot = z.infer<typeof authorizationSnapshotSchema>;
export function snapshot(
  assignment: Assignment,
  capabilityCeiling?: CapabilityId[],
): AuthorizationSnapshot {
  const parsed = assignmentSchema.parse(assignment);
  return authorizationSnapshotSchema.parse({
    ...parsed,
    ...(capabilityCeiling ? { capabilityCeiling } : {}),
    capabilities: capabilityUnion(parsed.roleIds).filter(
      (c) => !capabilityCeiling || capabilityCeiling.includes(c),
    ),
  });
}
export const legacyRoleIds = [
  "editor",
  "tester",
  "assetManager",
  "administrator",
] as const;
export const legacyRoleMap = {
  editor: "tenant.editor",
  tester: "tenant.tester",
  assetManager: "tenant.asset-manager",
  administrator: "tenant.administrator",
} as const;
/** Server-owned attribution, never an identity-provider or client role assertion. */
export const operatorContextSchema = z
  .object({
    kind: z.literal("operator-impersonation"),
    id: z.string().uuid(),
    tenantId: scopedId,
    actorOperatorId: z.string().uuid(),
    actorUsername: z.string().regex(/^[a-z0-9][a-z0-9._-]{2,63}$/),
    effectiveSubjectId: scopedId,
    effectiveRole: z.literal("administrator"),
    startedAt: z.number().int().safe().nonnegative(),
    expiresAt: z.number().int().safe().nonnegative(),
  })
  .strict()
  .refine(
    (v) =>
      v.effectiveSubjectId === `operator:${v.actorOperatorId}` &&
      v.expiresAt - v.startedAt >= 300000 &&
      v.expiresAt - v.startedAt <= 900000,
  );
export type OperatorContext = z.infer<typeof operatorContextSchema>;
export const principalSchema = z
  .object({
    subject: scopedId,
    applicationSessionId: z.uuid().optional(),
    integration: z
      .object({
        clientId: z.uuid(),
        tokenId: z.uuid(),
        scopes: z.array(z.enum(capabilityIds)),
        expiresAt: z.number().int().positive(),
      })
      .strict()
      .optional(),
    tenantId: scopedId,
    roles: z
      .array(z.enum(legacyRoleIds))
      .min(1)
      .max(4)
      .refine((v) => new Set(v).size === v.length),
    revision: z.number().int().positive(),
    authorization: authorizationSnapshotSchema.optional(),
    operatorContext: operatorContextSchema.optional(),
    profileMemberships: z
      .object({
        groupIds: z.array(scopedId).max(1000),
        companyIds: z.array(scopedId).max(1000),
      })
      .strict()
      .optional(),
  })
  .strict()
  .refine(
    (p) =>
      !p.authorization ||
      (p.authorization.subjectId === p.subject &&
        p.authorization.tenantId === p.tenantId &&
        p.authorization.authorizationRevision === p.revision &&
        JSON.stringify(p.authorization.roleIds) ===
          JSON.stringify(
            roleIds.filter((r) => p.roles.some((v) => legacyRoleMap[v] === r)),
          )),
  )
  .refine(
    (p) =>
      !p.integration ||
      (p.subject === `integration:${p.integration.clientId}` &&
        !p.applicationSessionId &&
        !p.operatorContext &&
        (!p.authorization ||
          JSON.stringify(p.authorization.capabilityCeiling) ===
            JSON.stringify(p.integration.scopes))),
  )
  .refine((p) =>
    p.operatorContext
      ? p.tenantId === p.operatorContext.tenantId &&
        p.subject === p.operatorContext.effectiveSubjectId &&
        p.revision === 1 &&
        p.roles.length === 1 &&
        p.roles[0] === "administrator"
      : !p.subject.startsWith("operator:"),
  );
export type AccessPrincipal = z.infer<typeof principalSchema>;
export class AccessError extends Error {
  constructor(
    readonly code: string,
    readonly status = 403,
  ) {
    super(code);
  }
}
export function requireCapability(
  p: AccessPrincipal,
  capability: CapabilityId,
) {
  const parsed = principalSchema.safeParse(p);
  if (
    !parsed.success ||
    !parsed.data.authorization?.capabilities.includes(capability)
  )
    throw new AccessError("TENANT_AUTHORIZATION_DENIED");
}
export const roleChangeSchema = z
  .object({
    subjectId: scopedId,
    roleIds: canonicalRoles,
    expectedAssignmentRevision: z.number().int().nonnegative(),
    expectedStateRevision: z.number().int().nonnegative(),
    confirmSelfDemotion: z.boolean().default(false),
  })
  .strict();
export type RoleChange = z.input<typeof roleChangeSchema>;
export const auditSchema = z
  .object({
    id: scopedId,
    tenantId: scopedId,
    actorId: scopedId,
    effectiveSubjectId: scopedId,
    operatorContext: operatorContextSchema.optional(),
    targetSubjectId: scopedId,
    before: canonicalRoles,
    after: canonicalRoles,
    assignmentRevision: z.number().int().positive(),
    authorizationRevision: z.number().int().positive(),
    stateRevision: z.number().int().positive(),
    at: z.string().datetime(),
    action: z.enum(["roles.replace", "host.adopt"]),
  })
  .strict()
  .refine(
    (a) =>
      !a.operatorContext ||
      (a.tenantId === a.operatorContext.tenantId &&
        a.actorId === a.operatorContext.actorOperatorId &&
        a.effectiveSubjectId === a.operatorContext.effectiveSubjectId),
  );
export type RoleAudit = z.infer<typeof auditSchema>;
export const pendingAccountRequestSchema = z
  .object({
    version: z.literal(1),
    tenantId: scopedId,
    id: scopedId,
    subjectId: scopedId,
    createdAt: z.string().datetime(),
    requestRevision: z.number().int().positive(),
    status: z.literal("pending"),
  })
  .strict();
export const accountRequestSchema = pendingAccountRequestSchema
  .extend({
    status: z.enum(["pending", "approved", "rejected"]),
    decidedAt: z.string().datetime().optional(),
  })
  .refine((r) =>
    r.status === "pending" ? r.decidedAt === undefined : !!r.decidedAt,
  );
export type PendingAccountRequest = z.infer<typeof pendingAccountRequestSchema>;
export type AccountRequest = z.infer<typeof accountRequestSchema>;
export const accountDecisionSchema = z.discriminatedUnion("action", [
  z
    .object({
      action: z.literal("approve"),
      roleIds: canonicalRoles.refine((r) => r.length > 0),
      expectedRequestRevision: z.number().int().positive(),
      expectedStateRevision: z.number().int().nonnegative(),
    })
    .strict(),
  z
    .object({
      action: z.literal("reject"),
      reason: z.string().trim().min(1).max(500),
      expectedRequestRevision: z.number().int().positive(),
      expectedStateRevision: z.number().int().nonnegative(),
    })
    .strict(),
]);
export type AccountDecision = z.input<typeof accountDecisionSchema>;
export const accountRequestAuditSchema = z
  .object({
    version: z.literal(1),
    id: scopedId,
    tenantId: scopedId,
    requestId: scopedId,
    subjectId: scopedId,
    actorId: scopedId,
    effectiveSubjectId: scopedId,
    operatorContext: operatorContextSchema.optional(),
    action: z.enum([
      "request.received",
      "request.updated",
      "request.approved",
      "request.rejected",
    ]),
    roleIds: canonicalRoles,
    reason: z
      .string()
      .min(1)
      .max(500)
      .refine((v) => v.trim() === v)
      .optional(),
    requestRevision: z.number().int().positive(),
    stateRevision: z.number().int().positive(),
    at: z.string().datetime(),
  })
  .strict()
  .refine(
    (a) =>
      !a.operatorContext ||
      (a.tenantId === a.operatorContext.tenantId &&
        a.actorId === a.operatorContext.actorOperatorId &&
        a.effectiveSubjectId === a.operatorContext.effectiveSubjectId),
  )
  .refine((a) =>
    a.action === "request.approved"
      ? a.roleIds.length > 0 && !a.reason
      : a.action === "request.rejected"
        ? a.roleIds.length === 0 && !!a.reason
        : a.roleIds.length === 0 && !a.reason,
  );
export type AccountRequestAudit = z.infer<typeof accountRequestAuditSchema>;
export interface AccountRequestList {
  version: 1;
  stateRevision: number;
  requests: AccountRequest[];
}
export interface AccountRequestDetail {
  request: AccountRequest;
  stateRevision: number;
  decision?: AccountRequestAudit;
}
export interface AccountDecisionResult extends AccountRequestDetail {
  replayed: boolean;
  assignment?: Assignment;
}
export const accessStateSchema = z
  .object({
    version: z.literal(1),
    revision: z.number().int().nonnegative(),
    assignments: z.array(assignmentSchema),
    audits: z.array(auditSchema),
    requests: z.array(accountRequestSchema).default([]),
    requestAudits: z.array(accountRequestAuditSchema).default([]),
  })
  .strict()
  .superRefine((s, ctx) => {
    if (
      new Set(
        s.assignments.map((a) => JSON.stringify([a.tenantId, a.subjectId])),
      ).size !== s.assignments.length ||
      new Set(s.audits.map((a) => a.id)).size !== s.audits.length ||
      s.audits.some((a) => a.stateRevision > s.revision)
    )
      ctx.addIssue({ code: "custom", message: "Invalid authorization state" });
    const requests = new Map(
      s.requests.map((r) => [JSON.stringify([r.tenantId, r.id]), r]),
    );
    if (
      requests.size !== s.requests.length ||
      new Set(s.requestAudits.map((a) => a.id)).size !== s.requestAudits.length
    )
      ctx.addIssue({
        code: "custom",
        message: "Invalid account request identities",
      });
    const histories = new Map<string, AccountRequestAudit[]>();
    for (const a of s.requestAudits) {
      const key = JSON.stringify([a.tenantId, a.requestId]),
        request = requests.get(key);
      if (
        !request ||
        request.subjectId !== a.subjectId ||
        a.stateRevision > s.revision
      )
        ctx.addIssue({
          code: "custom",
          message: "Invalid account request audit scope",
        });
      const history = histories.get(key) || [];
      history.push(a);
      histories.set(key, history);
    }
    for (const [key, r] of requests) {
      const history = histories.get(key) || [],
        terminal = history.filter(
          (a) =>
            a.action === "request.approved" || a.action === "request.rejected",
        ),
        latest = history.at(-1);
      if (
        !latest ||
        history[0]?.action !== "request.received" ||
        history.some(
          (a, i) =>
            i > 0 &&
            (a.requestRevision <= history[i - 1].requestRevision ||
              a.stateRevision <= history[i - 1].stateRevision ||
              a.action === "request.received"),
        ) ||
        latest.requestRevision !== r.requestRevision ||
        (r.status === "pending"
          ? terminal.length !== 0
          : terminal.length !== 1 ||
            terminal[0].action !== `request.${r.status}`)
      )
        ctx.addIssue({
          code: "custom",
          message: "Invalid account request lifecycle",
        });
      if (
        r.status !== "pending" &&
        latest &&
        (latest.at !== r.decidedAt || latest.action !== `request.${r.status}`)
      )
        ctx.addIssue({
          code: "custom",
          message: "Invalid account decision timestamp",
        });
      for (const decision of terminal) {
        const changes = s.audits.filter(
          (a) =>
            a.tenantId === decision.tenantId &&
            a.targetSubjectId === decision.subjectId &&
            a.stateRevision === decision.stateRevision &&
            a.action === "roles.replace",
        );
        if (
          decision.action === "request.approved"
            ? changes.length !== 1 ||
              changes[0].before.length !== 0 ||
              JSON.stringify(changes[0].after) !==
                JSON.stringify(decision.roleIds)
            : changes.length !== 0
        )
          ctx.addIssue({
            code: "custom",
            message: "Invalid account decision membership audit",
          });
      }
    }
  });
export type AccessState = z.infer<typeof accessStateSchema>;
export interface AccessStore {
  read(): Promise<AccessState>;
  compareAndSwap(next: AccessState, expectedRevision: number): Promise<void>;
}
